/**
 * Component for Warranty Rules settings.
 *
 * @component
 */

import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import React from 'react';
import { IconTray } from '../../../../components/CustomReturnsIcons';
import {
  canadapostServiceLevel,
  domesticServicesUPS,
  internationalServicesUPS,
  TYPE_OF_CUSTOMIZE_QUERY,
} from '../../../../constants/constants';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import { filterWarrantyProducts } from '../../../../helpers/warrantyProductSearch';
import WarrantyProducts from './components/WarrantyProducts';

const PRODUCT_SEARCH_DEBOUNCE_MS = 400;

const { Option } = Select;
const MAX_WARRANTY_PRODUCTS = 1000;
const enableCustomService = ['shipstation', 'shippo', 'easypost'];
const enableCustomServiceLevel = [
  'canadapost',
  'auspost',
  'shipstation',
  'shippo',
];

const defaultRule = {
  name: '',
  priority: 1,
  warrantyTimeLimit: 1,
  isActive: true,
  enableCategoryShippingMethods: false,
  enableCategoryActions: false,
  methodInStore: false,
  methodCustomerChoice: false,
  methodLabelCreation: false,
  methodLabelManual: false,
  methodCustomerPaidLabel: false,
  actionManualApproval: false,
  actionAutoApproval: false,
  actionCustomReturnAddress: false,
  actionCustomLabelFee: false,
  customLabelFeeType: 'fixed',
  customLabelFeeAmount: null,
  customLabelFeeCurrency: 'USD',
  actionCustomRestockingFee: false,
  customRestockingFeeType: 'fixed',
  customRestockingFeeAmount: null,
  customRestockingFeeCurrency: 'USD',
  actionCustomerPhotoUpload: false,
  stripePaymentsRule: false,
  stripeLabelCostRule: false,
  changeCarrier: false,
  customCarrier: null,
  customLabelCarrier: null,
  customLabelServicelevel: null,
  customDomesticService: null,
  customInternationalService: null,
  enableFreeReturn: false,
  customFreeReturnNumber: 1,
  warrantyProducts: [],
  // Return Options - which Action list a customer sees on the portal once they pick
  // "Warranty Claim" as their Return Type. See docs/warranty-return-type-plan.md.
  // Refund defaults to OFF for warranty rules (unlike Advanced Rules, where it defaults
  // on) - per BA decision, most warranty policies repair/replace rather than refund, so
  // merchants must opt in deliberately rather than discovering it's on by default.
  enableCategoryReturnOptions: false,
  optionRefund: false,
  optionCredit: false,
  optionExchange: false,
  optionAdvancedExchange: false,
  optionStoreWideExchange: false,
  optionCustom1: false,
  optionCustom2: false,
  optionCustom3: false,
  // Single per-rule customer note question - mirrors Advanced Rules' enableExtraNote.
  enableExtraNote: false,
  titleExtraNote: '',
  configExtraNote: {},
};

class WarrantyRules extends React.Component {
  static contextType = SubscriptionContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      rules: [],
      selectedRule: { ...defaultRule },
      showRuleForm: false,
      enableEditMode: false,
      storeWideModel: false,
      pendingWarrantyProducts: [],
      productSearchInput: '',
      productSearch: '',
      selectedProductsPage: 1,
      returnSettings: {},
      listStoreAddresses: [],
      listCarrier: [],
      listLabelCarrier: [],
      listLabelServiceLevel: [],
      listDomesticServicesUPS: [],
      listInternationalServicesUPS: [],
      isLoadingCarrierService: false,
      customAddressName: undefined,
    };
  }

  async componentDidMount() {
    await this.loadData();
  }

  loadData = async () => {
    try {
      const [rulesResponse, settingsResponse, rulesListDataResponse] = await Promise.all([
        axios.get('/api/returnSettings/warrantyRules'),
        axios.get('/api/returnSettings'),
        axios.get('/api/returnSettings/rules/listData'),
      ]);

      if (rulesResponse.data?.status === 'error') {
        message.error('Warranty Rules are not available on your plan.', 5);
        return this.setState({ loading: false });
      }

      this.setState({
        rules: rulesResponse.data?.rules || [],
        returnSettings: settingsResponse.data || {},
        listStoreAddresses: rulesListDataResponse.data?.listStoreAddresses || [],
        listCarrier: rulesListDataResponse.data?.carriers || [],
        loading: false,
      });
    } catch (err) {
      this.setState({ loading: false });
      message.error('Error retrieving your warranty rules. Try again.', 5);
    }
  };

  async componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selectedRule.customCarrier !== prevState.selectedRule.customCarrier ||
      this.state.selectedRule.customLabelCarrier !== prevState.selectedRule.customLabelCarrier
    ) {
      this.loadCarrierService();
    }
  }

  getRuleProducts = (rule = this.state.selectedRule) =>
    rule.warrantyProducts || rule.WarrantyRuleProducts || [];

  toggleRuleForm = () => {
    this.setState({
      showRuleForm: !this.state.showRuleForm,
      enableEditMode: false,
      selectedRule: {
        ...defaultRule,
        priority: this.state.rules.length + 1,
      },
      pendingWarrantyProducts: [],
      productSearchInput: '',
      productSearch: '',
      selectedProductsPage: 1,
    });
  };

  toggleEditMode = (record) => {
    this.setState({
      showRuleForm: true,
      enableEditMode: true,
      selectedRule: {
        ...defaultRule,
        ...record,
        warrantyProducts: record.WarrantyRuleProducts || [],
      },
      pendingWarrantyProducts: [],
      productSearchInput: '',
      productSearch: '',
      selectedProductsPage: 1,
    });
  };

  handleProductSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ productSearchInput: value });
    clearTimeout(this.productSearchDebounce);
    this.productSearchDebounce = setTimeout(() => {
      this.setState({ productSearch: value, selectedProductsPage: 1 });
    }, PRODUCT_SEARCH_DEBOUNCE_MS);
  };

  componentWillUnmount() {
    clearTimeout(this.productSearchDebounce);
  }

  handleValueChange = (field, value) => {
    this.setState((prevState) => ({
      selectedRule: {
        ...prevState.selectedRule,
        [field]: value,
      },
    }));
  };

  // Customer note (enableExtraNote/titleExtraNote/configExtraNote) handlers - mirror
  // client/src/pages/dashboard/settings/rules/Rules.js exactly.
  onUpdateType = (type) => {
    const configExtraNote = {
      ...this.state.selectedRule.configExtraNote,
      type,
      options: this.state.selectedRule.configExtraNote?.options || [''],
    };
    this.setState((prevState) => ({
      selectedRule: {
        ...prevState.selectedRule,
        configExtraNote,
      },
    }));
  };

  handleAddOption = () => {
    const configExtraNote = this.state.selectedRule.configExtraNote || {};
    if (!configExtraNote.options) {
      configExtraNote.options = [];
    }
    if (configExtraNote.options.length < 3) {
      configExtraNote.options.push('');
      this.setState({
        selectedRule: {
          ...this.state.selectedRule,
          configExtraNote,
        },
      });
    }
  };

  handleMinusOption = () => {
    const configExtraNote = this.state.selectedRule.configExtraNote || {};
    if (!configExtraNote.options) {
      configExtraNote.options = [];
    }
    if (configExtraNote.options.length > 1) {
      configExtraNote.options.pop();
      this.setState({
        selectedRule: {
          ...this.state.selectedRule,
          configExtraNote,
        },
      });
    }
  };

  handleChangeOptions = (e) => {
    const configExtraNote = this.state.selectedRule.configExtraNote || {};
    configExtraNote.options[Number(e.target.id)] = e.target.value;
    this.setState({
      selectedRule: {
        ...this.state.selectedRule,
        configExtraNote,
      },
    });
  };

  onChangeRequired = (e) => {
    const configExtraNote = {
      ...this.state.selectedRule.configExtraNote,
      isRequired: e.target.checked,
    };
    this.setState({
      selectedRule: {
        ...this.state.selectedRule,
        configExtraNote,
      },
    });
  };

  handleSubmit = async () => {
    const { selectedRule, enableEditMode } = this.state;
    if (!selectedRule.name) {
      return message.error('The rule name field cannot be empty.', 3);
    }
    if (!selectedRule.warrantyTimeLimit) {
      return message.error('Please set a warranty time limit.', 3);
    }
    if (
      (selectedRule.customRestockingFeeType === 'percentage' ||
        selectedRule.customRestockingFeeType === 'percentageTax') &&
      Number(selectedRule.customRestockingFeeAmount) > 100
    ) {
      return message.error("Percentage amount can't be greater then 100", 5);
    }
    if (this.getRuleProducts().length === 0) {
      return message.error('At least one product should be included in warranty.', 3);
    }

    const endpoint = enableEditMode
      ? '/api/returnSettings/warrantyRules/update'
      : '/api/returnSettings/warrantyRules/create';
    const response = await axios.post(endpoint, selectedRule);
    if (response.data?.status === 'error') {
      return message.error('Error saving your warranty rule. Please try again.', 5);
    }

    message.success(`Warranty rule ${enableEditMode ? 'updated' : 'created'} successfully.`, 3);
    await this.loadData();
    this.setState({
      showRuleForm: false,
      enableEditMode: false,
      selectedRule: { ...defaultRule },
    });
  };

  handleConfirm = async (record) => {
    const response = await axios.post('/api/returnSettings/warrantyRules/update', {
      ...record,
      isActive: !record.isActive,
      warrantyProducts: record.WarrantyRuleProducts || [],
    });
    if (response.data?.status === 'error') {
      return message.error('Error toggling rule. Please try again.', 5);
    }
    await this.loadData();
  };

  deleteRule = async (record) => {
    const response = await axios.post('/api/returnSettings/warrantyRules/delete', {
      id: record.id,
    });
    if (response.data?.status === 'error') {
      return message.error('Error deleting rule. Please try again.', 5);
    }
    message.success('Warranty rule deleted successfully.', 3);
    await this.loadData();
  };

  handleStoreWide = () => {
    this.setState({
      storeWideModel: !this.state.storeWideModel,
      pendingWarrantyProducts: [],
    });
  };

  getWarrantyProductField = (products, variant, fallbackProduct) => {
    const selectedProduct =
      fallbackProduct || products.find((product) => product.id === variant.product_id);

    const field = {};
    if (selectedProduct?.variants?.length > 1) {
      field.variantTitle = variant.title;
    }
    field.title = selectedProduct?.title;
    field.productId = selectedProduct?.id;
    field.variantId = variant.id;
    return field;
  };

  warrantyVariantExists = (variantId, products = this.getRuleProducts()) =>
    products?.some((product) => String(product.variantId) === String(variantId));

  getWarrantySelectionCount = () =>
    this.getRuleProducts().length + this.state.pendingWarrantyProducts.length;

  showWarrantyProductLimitError = () =>
    message.error(`A warranty rule can include up to ${MAX_WARRANTY_PRODUCTS} products.`, 3);

  setRuleProducts = (warrantyProducts) => {
    this.handleValueChange('warrantyProducts', warrantyProducts);
  };

  handleWarrantyProduct = async (products, variant, fallbackProduct) => {
    if (this.warrantyVariantExists(variant.id)) return;
    if (this.getWarrantySelectionCount() >= MAX_WARRANTY_PRODUCTS) {
      return this.showWarrantyProductLimitError();
    }

    const field = this.getWarrantyProductField(products, variant, fallbackProduct);
    this.setRuleProducts([...this.getRuleProducts(), field]);
  };

  handleToggleWarrantyProduct = (products, variant, checked, fallbackProduct) => {
    const pendingWarrantyProducts = this.state.pendingWarrantyProducts || [];
    const isPending = pendingWarrantyProducts.some(
      (product) => String(product.variantId) === String(variant.id),
    );
    const shouldAdd = checked === undefined ? !isPending : checked;

    if (!shouldAdd) {
      return this.setState({
        pendingWarrantyProducts: pendingWarrantyProducts.filter(
          (product) => String(product.variantId) !== String(variant.id),
        ),
      });
    }

    if (this.warrantyVariantExists(variant.id) || isPending) return;
    if (this.getWarrantySelectionCount() >= MAX_WARRANTY_PRODUCTS) {
      return this.showWarrantyProductLimitError();
    }

    this.setState({
      pendingWarrantyProducts: [
        ...pendingWarrantyProducts,
        this.getWarrantyProductField(products, variant, fallbackProduct),
      ],
    });
  };

  handleToggleWarrantyProductVariants = (products, product, checked) => {
    const pendingWarrantyProducts = this.state.pendingWarrantyProducts || [];
    const selectableVariants = product.variants.filter(
      (variant) => !this.warrantyVariantExists(variant.id),
    );
    const selectableVariantIds = selectableVariants.map((variant) => String(variant.id));
    const pendingVariantIds = pendingWarrantyProducts.map((product) => String(product.variantId));
    const allPending = selectableVariantIds.length > 0 &&
      selectableVariantIds.every((variantId) => pendingVariantIds.includes(variantId));
    const shouldAdd = checked === undefined ? !allPending : checked;
    const pendingWithoutProductVariants = pendingWarrantyProducts.filter(
      (pendingProduct) => !selectableVariantIds.includes(String(pendingProduct.variantId)),
    );

    if (!shouldAdd) {
      return this.setState({ pendingWarrantyProducts: pendingWithoutProductVariants });
    }
    const nextSelectionCount = this.getRuleProducts().length
      + pendingWithoutProductVariants.length
      + selectableVariants.length;
    if (nextSelectionCount > MAX_WARRANTY_PRODUCTS) {
      return this.showWarrantyProductLimitError();
    }

    this.setState({
      pendingWarrantyProducts: [
        ...pendingWithoutProductVariants,
        ...selectableVariants.map((variant) =>
          this.getWarrantyProductField(products, variant, product),
        ),
      ],
    });
  };

  handleToggleWarrantyProducts = (products, checked) => {
    const pendingWarrantyProducts = this.state.pendingWarrantyProducts || [];
    const selectableVariants = products.flatMap((product) =>
      product.variants
        .filter((variant) => !this.warrantyVariantExists(variant.id))
        .map((variant) => ({ product, variant })),
    );
    const selectableVariantIds = selectableVariants.map(({ variant }) => String(variant.id));
    const pendingVariantIds = pendingWarrantyProducts.map((product) => String(product.variantId));
    const allPending = selectableVariantIds.length > 0 &&
      selectableVariantIds.every((variantId) => pendingVariantIds.includes(variantId));
    const shouldAdd = checked === undefined ? !allPending : checked;
    const pendingWithoutVisibleVariants = pendingWarrantyProducts.filter(
      (product) => !selectableVariantIds.includes(String(product.variantId)),
    );

    if (!shouldAdd) {
      return this.setState({ pendingWarrantyProducts: pendingWithoutVisibleVariants });
    }
    const nextSelectionCount = this.getRuleProducts().length
      + pendingWithoutVisibleVariants.length
      + selectableVariants.length;
    if (nextSelectionCount > MAX_WARRANTY_PRODUCTS) {
      return this.showWarrantyProductLimitError();
    }

    this.setState({
      pendingWarrantyProducts: [
        ...pendingWithoutVisibleVariants,
        ...selectableVariants.map(({ product, variant }) =>
          this.getWarrantyProductField(products, variant, product),
        ),
      ],
    });
  };

  handleAddSelectedWarrantyProducts = () => {
    const pendingWarrantyProducts = this.state.pendingWarrantyProducts || [];
    if (pendingWarrantyProducts.length === 0) return;

    const warrantyProducts = [...this.getRuleProducts()];
    pendingWarrantyProducts.forEach((product) => {
      if (!warrantyProducts.some((field) => String(field.variantId) === String(product.variantId))) {
        warrantyProducts.push(product);
      }
    });

    this.setState({ pendingWarrantyProducts: [] });
    this.setRuleProducts(warrantyProducts);
  };

  handleDeleteWarrantyProduct = (variant) => {
    this.setRuleProducts(
      this.getRuleProducts().filter(
        (product) => String(product.variantId) !== String(variant.variantId),
      ),
    );
  };


  loadCustomReturnAddress = (addressId) => {
    const storeAddress = this.state.listStoreAddresses.find(
      (address) => address.id === addressId,
    );
    if (!storeAddress) return;

    this.setState((prevState) => ({
      customAddressName: storeAddress.locationName,
      selectedRule: {
        ...prevState.selectedRule,
        customReturnAddressName: storeAddress.locationName,
        customReturnAddressCompany: storeAddress.company,
        customReturnAddressStreet1: storeAddress.streetAddress,
        customReturnAddressStreet2: storeAddress.optional,
        customReturnAddressCity: storeAddress.city,
        customReturnAddressZip: storeAddress.zipCode,
        customReturnAddressProvince: storeAddress.state,
        customReturnAddressProvinceCode: storeAddress.state,
        customReturnAddressCountry: storeAddress.country,
        customReturnAddressCountryCode: storeAddress.country,
        customReturnAddressPhone: storeAddress.phone,
      },
    }));
  };

  loadCarrierService = async () => {
    const carrier = this.state.selectedRule?.customCarrier;
    if (!carrier) return;

    this.setState({ isLoadingCarrierService: true });
    try {
      if (carrier === 'canadapost') {
        this.setState({
          listLabelServiceLevel: canadapostServiceLevel,
          listLabelCarrier: [],
        });
      }

      if (carrier === 'auspost') {
        const response = await axios.post('/api/integrations/auspost/delivery-services', {});
        const auspostServiceLevel = response.data.deliveryServiceList?.map((service) => ({
          serviceCode: service.product_id,
          name: service.name,
        })) || [];
        this.setState({
          listLabelServiceLevel: auspostServiceLevel,
          listLabelCarrier: [],
        });
      }

      if (carrier === 'shipstation') {
        const response = await axios.post('/api/integrations/shipstation/carriers', {
          labelCarrier: this.state.selectedRule?.customLabelCarrier || null,
        });
        this.setState({
          listLabelCarrier: response.data.carrierListShipStation.map((labelCarrier) => ({
            labelCarrierCode: labelCarrier.code,
            name: labelCarrier.name,
          })),
          listLabelServiceLevel: response.data.ratesListShipStation.map((service) => ({
            serviceCode: service.code,
            name: service.name,
          })),
        });
      }

      if (carrier === 'ups') {
        this.setState({
          listDomesticServicesUPS: domesticServicesUPS,
          listInternationalServicesUPS: internationalServicesUPS,
          listLabelCarrier: [],
          listLabelServiceLevel: [],
        });
      }

      if (carrier === 'shippo') {
        const selectedCarrier = this.state.selectedRule?.customLabelCarrier;
        const carrierResponse = await axios.post('/api/integrations/shippo/carriers', {});
        let serviceLevelResponse = { data: { ratesListShippo: [] } };
        if (selectedCarrier) {
          serviceLevelResponse = await axios.post(
            `/api/integrations/shippo/servicelevels/${selectedCarrier}`,
            {},
          );
        }
        this.setState({
          listLabelCarrier: carrierResponse.data.carrierListShippo.map((labelCarrier) => ({
            labelCarrierCode: labelCarrier.code,
            name: labelCarrier.name,
          })),
          listLabelServiceLevel: serviceLevelResponse.data.ratesListShippo.map((service) => ({
            serviceCode: service.code,
            name: service.name,
          })),
        });
      }

      if (carrier === 'easypost') {
        const response = await axios.post('/api/integrations/easypost/carriers', {});
        this.setState({
          listLabelCarrier: response.data.carrierListEasyPost.map((labelCarrier) => ({
            labelCarrierCode: labelCarrier.id,
            name: labelCarrier.description,
          })),
          listLabelServiceLevel: [],
        });
      }
    } catch (err) {
      message.error('Error requesting carrier configuration. Please try again.', 5);
    } finally {
      this.setState({ isLoadingCarrierService: false });
    }
  };

  renderCarrierOptions = () =>
    this.state.listCarrier.map((carrier) => (
      <Option key={carrier.labelService} value={carrier.labelService}>
        {carrier.name}
      </Option>
    ));

  renderLabelCarrierOptions = () => {
    const auto = { labelCarrierCode: null, name: 'Default Setting' };
    return [auto, ...this.state.listLabelCarrier].map((carrier) => (
      <Option key={carrier.labelCarrierCode} value={carrier.labelCarrierCode}>
        {carrier.name}
      </Option>
    ));
  };

  renderLabelServiceLevelOptions = () => {
    const auto = { serviceCode: null, name: 'Default Setting' };
    return [auto, ...this.state.listLabelServiceLevel].map((service) => (
      <Option key={service.serviceCode} value={service.serviceCode}>
        {service.name}
      </Option>
    ));
  };

  renderDomesticServiceOptions = () => {
    const auto = { serviceCode: null, name: 'Default Setting' };
    return [auto, ...this.state.listDomesticServicesUPS].map((service) => (
      <Option key={service.serviceCode} value={service.serviceCode}>
        {service.name}
      </Option>
    ));
  };

  renderInternationalServiceOptions = () => {
    const auto = { serviceCode: null, name: 'Default Setting' };
    return [auto, ...this.state.listInternationalServicesUPS].map((service) => (
      <Option key={service.serviceCode} value={service.serviceCode}>
        {service.name}
      </Option>
    ));
  };

  renderSwitch = (field, label) => (
    <Row type="flex" align="middle" className="u-marginBottom--sm">
      <Switch
        checked={!!this.state.selectedRule[field]}
        onChange={(checked) => this.handleValueChange(field, checked)}
      />
      <span className="u-marginLeft--sm">{label}</span>
    </Row>
  );

  renderCheckbox = (field, label, disabled = false) => (
    <Checkbox
      checked={!!this.state.selectedRule[field]}
      onChange={(event) => this.handleValueChange(field, event.target.checked)}
      disabled={disabled}
    >
      {label}
    </Checkbox>
  );

  renderInput = (field, placeholder, type = 'text') => (
    <Input
      placeholder={placeholder}
      type={type}
      value={this.state.selectedRule[field]}
      onChange={(event) => this.handleValueChange(field, event.target.value)}
    />
  );

  renderRuleForm = () => {
    const selectedProducts = this.getRuleProducts();
    const displayedProducts = filterWarrantyProducts(selectedProducts, this.state.productSearch);
    const selectedRule = this.state.selectedRule;
    const columns = [
      {
        title: 'Product Name',
        dataIndex: 'title',
        key: 'productName',
        render: (title, product) => (
          <Tag color="gold">
            {product.variantTitle ? `${title}(${product.variantTitle})` : title}
          </Tag>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_text, record) => (
          <Button ghost size="small" onClick={() => this.handleDeleteWarrantyProduct(record)}>
            Delete
          </Button>
        ),
      },
    ];

    return (
      <Form>
        <Card size="small" title="Rule settings" className="u-marginBottom--md">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <label>Rule name</label>
              {this.renderInput('name', 'Rule name')}
            </Col>
            <Col xs={24} md={12}>
              <label>Rule priority</label>
              {this.renderInput('priority', 'Priority', 'number')}
            </Col>
            <Col xs={24} md={12}>
              <label>Warranty time limit (days)</label>
              {this.renderInput('warrantyTimeLimit', 'Warranty time limit', 'number')}
            </Col>
            <Col span={24}>
              {this.renderSwitch('isActive', 'Enable rule')}
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Add Products" className="u-marginBottom--md">
          <p>Select products to apply this warranty rule to them at once.</p>
          <Button onClick={this.handleStoreWide}>Select Products</Button>
        </Card>

        <Card size="small" title="Selected Products" className="u-marginBottom--md">
          <Input
            allowClear
            placeholder="Search selected products"
            className="u-marginBottom--sm"
            value={this.state.productSearchInput}
            onChange={this.handleProductSearchChange}
          />
          <Table
            columns={columns}
            dataSource={displayedProducts}
            rowKey={(record) => record.variantId}
            pagination={{
              pageSize: 5,
              current: this.state.selectedProductsPage,
              onChange: (page) => this.setState({ selectedProductsPage: page }),
            }}
            locale={{
              emptyText: this.state.productSearch
                ? 'No products found matching your search.'
                : 'No products selected yet.',
            }}
          />
        </Card>

        <div className="ContentPanel u-marginBottom--md">
          <h3>Rule effect</h3>
          <p className="u-marginBottom--md">
            What should change when the rule triggers?
          </p>

          <Divider>Return options</Divider>
          <p>
            Which resolutions a customer can choose from once they select
            "Warranty Claim" on the portal. Advanced Rules are not consulted for
            warranty claims - only what's configured here applies.
          </p>
          {this.renderSwitch('enableCategoryReturnOptions', 'Make changes to this section?')}
          <Row className="u-rowGap--sm">
            <Col span={24}>{this.renderCheckbox('optionRefund', 'Refund', !selectedRule.enableCategoryReturnOptions)}</Col>
            <Col span={24}>{this.renderCheckbox('optionCredit', 'Store Credit', !selectedRule.enableCategoryReturnOptions)}</Col>
            <Col span={24}>{this.renderCheckbox('optionExchange', 'Exchange', !selectedRule.enableCategoryReturnOptions)}</Col>
            {this.context.featureAdvancedExchanges ? (
              <Col span={24}>{this.renderCheckbox('optionAdvancedExchange', 'Collection Exchange', !selectedRule.enableCategoryReturnOptions)}</Col>
            ) : null}
            {this.context.featureStoreWideExchange ? (
              <Col span={24}>{this.renderCheckbox('optionStoreWideExchange', 'Store-Wide Exchange', !selectedRule.enableCategoryReturnOptions)}</Col>
            ) : null}
            <Col span={24}>{this.renderCheckbox('optionCustom1', 'Custom Option 1', !selectedRule.enableCategoryReturnOptions)}</Col>
            <Col span={24}>{this.renderCheckbox('optionCustom2', 'Custom Option 2', !selectedRule.enableCategoryReturnOptions)}</Col>
            <Col span={24}>{this.renderCheckbox('optionCustom3', 'Custom Option 3', !selectedRule.enableCategoryReturnOptions)}</Col>
          </Row>

          <Divider>Return methods</Divider>
          {this.renderSwitch('enableCategoryShippingMethods', 'Make changes to this section?')}
          <Row className="u-rowGap--sm">
            <Col span={24}>{this.renderCheckbox('methodCustomerChoice', 'Customer is responsible for shipping', !selectedRule.enableCategoryShippingMethods)}</Col>
            <Col span={24}>{this.renderCheckbox('methodLabelCreation', 'Pre-Paid Return Label (automatic)', !selectedRule.enableCategoryShippingMethods)}</Col>
            <Col span={24}>{this.renderCheckbox('methodLabelManual', 'Pre-Paid Return Label (manual)', !selectedRule.enableCategoryShippingMethods)}</Col>
            <Col span={24}>{this.renderCheckbox('methodCustomerPaidLabel', 'Customer Paid Return Label', !selectedRule.enableCategoryShippingMethods)}</Col>
            <Col span={24}>{this.renderCheckbox('methodInStore', 'In-Store Return', !selectedRule.enableCategoryShippingMethods)}</Col>
          </Row>

          <Divider>Other options</Divider>
          {this.renderSwitch('enableCategoryActions', 'Make changes to this section?')}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title="Manual Approval"
                extra={
                  <Tooltip title="Helpful when you auto-approve returns but want to manually approve selected warranty returns.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionManualApproval', 'Manually approve return ("Pending" status)')}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Auto Approve"
                extra={
                  <Tooltip title="Use this option to auto-approve warranty returns matching this rule.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionAutoApproval', 'Auto approve return ("Approved" status)')}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Custom Return Address"
                extra={
                  <Tooltip title="Use a custom return address when this warranty rule matches. This address is available in the dynamic variable {RETURN_ADDRESS} for email templates and for generating shipping labels when prepaid labels are enabled.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionCustomReturnAddress', 'Use a custom return address')}
                {selectedRule.actionCustomReturnAddress && (
                  <Row gutter={[16, 16]} className="u-marginTop--sm">
                    <Col span={24}>
                      <h4>Load an address from your multiple addresses</h4>
                      <Select
                        value={this.state.customAddressName}
                        style={{ width: '100%' }}
                        onChange={this.loadCustomReturnAddress}
                      >
                        {this.state.listStoreAddresses.map((address) => (
                          <Option key={address.id} value={address.id}>
                            {address.locationName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressName', 'Full name')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressCompany', 'Company')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressPhone', 'Phone')}</Col>
                    <Col xs={24} md={12}>{this.renderInput('customReturnAddressStreet1', 'Address line 1')}</Col>
                    <Col xs={24} md={12}>{this.renderInput('customReturnAddressStreet2', 'Address line 2')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressCity', 'City')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressProvince', 'State/Province')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressProvinceCode', 'State/Province code')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressZip', 'Zip')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressCountry', 'Country')}</Col>
                    <Col xs={24} md={8}>{this.renderInput('customReturnAddressCountryCode', 'Country code')}</Col>
                  </Row>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Custom Label Fee"
                extra={
                  <Tooltip title="Set a label fee that applies only to warranty returns matching this rule.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionCustomLabelFee', 'Set a custom label fee')}
                {selectedRule.actionCustomLabelFee && (
                  <React.Fragment>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 20 }}>
                      <Col span={24}>
                        <Select
                          value={selectedRule.customLabelFeeType || 'fixed'}
                          style={{ width: '100%' }}
                          placeholder="Select Type"
                          onChange={(value) => this.handleValueChange('customLabelFeeType', value)}
                          suffixIcon={<span>Type</span>}
                        >
                          <Option value="fixed" key="fixed">
                                Fixed Fee in your Store's default currency
                          </Option>
                        </Select>
                      </Col>
                    </Row>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 20 }}>
                      <Col span={24}>
                        <InputNumber
                          min={0}
                          step={0.1}
                          precision={2}
                          placeholder="Enter amount"
                          style={{ width: '100%' }}
                          value={selectedRule.customLabelFeeAmount}
                          onChange={(value) => this.handleValueChange('customLabelFeeAmount', value)}
                        />
                      </Col>
                    </Row>
                  </React.Fragment>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Custom Restocking Fee"
                extra={
                  <Tooltip title="Set a restocking fee that applies only to warranty returns matching this rule.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionCustomRestockingFee', 'Set a custom restocking fee')}
                {selectedRule.actionCustomRestockingFee && (
                  <React.Fragment>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 20 }}>
                      <Col span={24}>
                        <Select
                          value={selectedRule.customRestockingFeeType || 'fixed'}
                          style={{ width: '100%' }}
                          placeholder="Select Type"
                          onChange={(value) => this.handleValueChange('customRestockingFeeType', value)}
                          suffixIcon={<span>Type</span>}
                        >
                          <Option key="fixed" value="fixed">
                                Fixed Fee in your Store's default currency
                          </Option>
                          <Option key="perItem" value="perItem">
                                Per-item Fee in your Store's default currency
                          </Option>
                          <Option key="percentage" value="percentage">
                                Restocking Fee in Percentage
                          </Option>
                          <Option key="percentageTax" value="percentageTax">
                                Restocking Fee include tax in percentage
                          </Option>
                        </Select>
                      </Col>
                    </Row>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 20 }}>
                      <Col span={24}>
                        <InputNumber
                          min={0}
                          step={0.1}
                          precision={2}
                          placeholder="Enter amount"
                          style={{ width: '100%' }}
                          value={selectedRule.customRestockingFeeAmount}
                          onChange={(value) => this.handleValueChange('customRestockingFeeAmount', value)}
                        />
                      </Col>
                    </Row>
                  </React.Fragment>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Custom Free Return"
                extra={
                  <Tooltip title="Set how many warranty returns per order should be free when this rule matches.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('enableFreeReturn', 'Set a custom free return per order')}
                {selectedRule.enableFreeReturn && (
                  <Row type="flex" justify="center" align="top" style={{ marginTop: 20 }}>
                    <Col span={24}>
                      <InputNumber
                        min={0}
                        step={1}
                        placeholder="Enter number of limit return"
                        style={{ width: '100%' }}
                        value={selectedRule.customFreeReturnNumber || 1}
                        onChange={(value) => this.handleValueChange('customFreeReturnNumber', value)}
                      />
                    </Col>
                  </Row>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Stripe Payments"
                extra={
                  <Tooltip title="Use this option to enable Stripe payments for warranty returns matching this rule.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('stripePaymentsRule', 'Enable Stripe Payments')}
                {selectedRule.stripePaymentsRule && (
                  <Row gutter={[16, 16]} className="u-marginTop--sm">
                    <Col span={24}>{this.renderCheckbox('stripeLabelCostRule', 'Charge label cost with Stripe')}</Col>
                  </Row>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Customer Media Upload"
                extra={
                  <Tooltip title="Ask customers for photos and videos when this warranty rule matches.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('actionCustomerPhotoUpload', 'Ask the customer to upload photos and videos for the return')}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Add customer note"
                extra={
                  <Tooltip title="Use this option to add an additional customer note for additional information about the warranty return from the customer.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                <Checkbox
                  checked={!!selectedRule.enableExtraNote}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    // Default configExtraNote.type to 'note' as soon as this is turned on, so a
                    // merchant who never touches the "Choose your type" dropdown (it already
                    // displays "Note" as selected) still gets an explicit, saved type instead of
                    // undefined - otherwise the portal has nothing to match against and silently
                    // renders the note field as nothing.
                    const configExtraNote =
                      enabled && !selectedRule.configExtraNote?.type
                        ? { ...selectedRule.configExtraNote, type: 'note', options: selectedRule.configExtraNote?.options || [''] }
                        : selectedRule.configExtraNote;
                    this.setState((prevState) => ({
                      selectedRule: {
                        ...prevState.selectedRule,
                        enableExtraNote: enabled,
                        configExtraNote,
                      },
                    }));
                  }}
                >
                  Add customer note
                </Checkbox>
                {selectedRule.enableExtraNote && (
                  <React.Fragment>
                    <Row type="flex" align="top" style={{ marginTop: 10 }}>
                      <Checkbox
                        checked={selectedRule.configExtraNote?.isRequired || false}
                        onChange={this.onChangeRequired}
                      >
                            Required?
                      </Checkbox>
                    </Row>
                    <Row type="flex" align="top" style={{ marginTop: 15 }}>
                          Your query here
                    </Row>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 10 }}>
                      <Col span={24}>
                        <Input
                          placeholder="Example: Please select all that apply down below in terms of your satisfaction"
                          style={{ width: '100%' }}
                          defaultValue={selectedRule.titleExtraNote}
                          onChange={(e) => this.handleValueChange('titleExtraNote', e.target.value)}
                        />
                      </Col>
                    </Row>
                    <Row type="flex" align="top" style={{ marginTop: 10 }}>
                          Choose your type
                    </Row>
                    <Row type="flex" justify="center" align="top" style={{ marginTop: 10 }}>
                      <Select
                        placeholder="Choose your type"
                        onChange={this.onUpdateType}
                        style={{ width: '100%' }}
                        value={selectedRule.configExtraNote?.type || 'note'}
                      >
                        {TYPE_OF_CUSTOMIZE_QUERY.map((i, key) => (
                          <Option key={key} value={i.value}>
                            {i.label}
                          </Option>
                        ))}
                      </Select>
                    </Row>
                    {['checkbox', 'radio-button'].includes(selectedRule.configExtraNote?.type) && (
                      <React.Fragment>
                        <Row
                          span={24}
                          style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}
                        >
                          {selectedRule.configExtraNote.type === 'checkbox'
                                ? 'Checkboxes (up to 3)'
                                : 'Radio buttons (up to 3)'}
                              :{' '}
                          <PlusCircleOutlined
                            style={{ fontSize: '20px', padding: '2px' }}
                            onClick={this.handleAddOption}
                          />
                          <MinusCircleOutlined
                            style={{ fontSize: '20px', padding: '2px' }}
                            onClick={this.handleMinusOption}
                          />
                        </Row>
                        <Row span={24}>
                          {selectedRule.configExtraNote.options &&
                                selectedRule.configExtraNote.options.map((i, key) => (
                                  <Col span={8} key={key} style={{ padding: '3px' }}>
                                    <Row>Option {key + 1} </Row>
                                    <Row>
                                      <Input
                                        span={22}
                                        id={key.toString()}
                                        onChange={this.handleChangeOptions}
                                        defaultValue={i}
                                      />
                                    </Row>
                                  </Col>
                                ))}
                        </Row>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title="Carrier Preference"
                extra={
                  <Tooltip title="Use this option to set a specific carrier for warranty returns matching this rule. Advanced Rules carrier preferences still have higher priority.">
                    <InfoCircleOutlined />
                  </Tooltip>
                }
              >
                {this.renderCheckbox('changeCarrier', 'Choose a specific carrier for this rule')}
                {selectedRule.changeCarrier && (
                  <React.Fragment>
                    <Row className="u-marginTop--sm">
                      <Col span={24}>
                        <Select
                          value={selectedRule.customCarrier}
                          placeholder="Select Carrier"
                          onChange={(value) => {
                            this.handleValueChange('customCarrier', value);
                            this.handleValueChange('customLabelCarrier', null);
                            this.handleValueChange('customLabelServicelevel', null);
                            this.handleValueChange('customDomesticService', null);
                            this.handleValueChange('customInternationalService', null);
                          }}
                          style={{ width: '100%' }}
                          notFoundContent={
                            <div className="flex-row flex-middle-xxs flex-center-xxs">
                              <div className="flex-col-xxs-12">
                                <IconTray size="md" />
                              </div>
                              <div>
                                <p className="TextBody TextBody--xxxs">No Data</p>
                              </div>
                            </div>
                          }
                        >
                          {this.renderCarrierOptions()}
                        </Select>
                      </Col>
                    </Row>

                    {enableCustomService.includes(selectedRule.customCarrier) && (
                      <Row gutter={[16, 16]} className="u-marginTop--sm">
                        <Col xs={24} md={6}>
                          <p style={{ paddingTop: '10px' }}>Select Carrier:</p>
                        </Col>
                        <Col xs={24} md={18}>
                          <Select
                            placeholder="Select Carrier"
                            value={selectedRule.customLabelCarrier || null}
                            onChange={(value) => this.handleValueChange('customLabelCarrier', value)}
                            style={{ width: '100%' }}
                            disabled={this.state.isLoadingCarrierService}
                          >
                            {this.renderLabelCarrierOptions()}
                          </Select>
                        </Col>
                      </Row>
                    )}

                    {enableCustomServiceLevel.includes(selectedRule.customCarrier) && (
                      <Row gutter={[16, 16]} className="u-marginTop--sm">
                        <Col xs={24} md={6}>
                          <p style={{ paddingTop: '10px' }}>
                            {selectedRule.customCarrier === 'auspost' ? 'Delivery service' : 'Select Service'}:
                          </p>
                        </Col>
                        <Col xs={24} md={18}>
                          <Select
                            placeholder="Select Service Level"
                            value={selectedRule.customLabelServicelevel || null}
                            onChange={(value) => this.handleValueChange('customLabelServicelevel', value)}
                            style={{ width: '100%' }}
                            disabled={this.state.isLoadingCarrierService}
                          >
                            {this.renderLabelServiceLevelOptions()}
                          </Select>
                        </Col>
                      </Row>
                    )}

                    {selectedRule.customCarrier === 'ups' && (
                      <React.Fragment>
                        <Row gutter={[16, 16]} className="u-marginTop--sm">
                          <Col xs={24} md={6}>
                            <p style={{ paddingTop: '10px' }}>Select Domestic Services:</p>
                          </Col>
                          <Col xs={24} md={18}>
                            <Select
                              placeholder="Select Domestic Services"
                              value={selectedRule.customDomesticService || null}
                              onChange={(value) => this.handleValueChange('customDomesticService', value)}
                              style={{ width: '100%' }}
                              disabled={this.state.isLoadingCarrierService}
                            >
                              {this.renderDomesticServiceOptions()}
                            </Select>
                          </Col>
                        </Row>
                        <Row gutter={[16, 16]} className="u-marginTop--sm">
                          <Col xs={24} md={6}>
                            <p style={{ paddingTop: '10px' }}>Select International Services:</p>
                          </Col>
                          <Col xs={24} md={18}>
                            <Select
                              placeholder="Select International Services"
                              value={selectedRule.customInternationalService || null}
                              onChange={(value) => this.handleValueChange('customInternationalService', value)}
                              style={{ width: '100%' }}
                              disabled={this.state.isLoadingCarrierService}
                            >
                              {this.renderInternationalServiceOptions()}
                            </Select>
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}

                    {selectedRule.customCarrier === 'easypost' && (
                      <Row gutter={[16, 16]} className="u-marginTop--sm">
                        <Col xs={24} md={6}>
                          <p style={{ paddingTop: '10px' }}>Select Service:</p>
                        </Col>
                        <Col xs={24} md={18}>
                          <Input
                            placeholder="Enter custom service level"
                            value={selectedRule.customLabelServicelevel || ''}
                            onChange={(event) =>
                              this.handleValueChange('customLabelServicelevel', event.target.value)
                            }
                            disabled={this.state.isLoadingCarrierService}
                          />
                        </Col>
                      </Row>
                    )}
                  </React.Fragment>
                )}
              </Card>
            </Col>
          </Row>
        </div>

        {this.state.storeWideModel && (
          <Modal
            className="removeFormMargin"
            width={730}
            title="Select Products"
            visible={this.state.storeWideModel}
            onCancel={this.handleStoreWide}
            footer={[
              <Button
                key="add-selected"
                type="primary"
                onClick={this.handleAddSelectedWarrantyProducts}
                disabled={!this.state.pendingWarrantyProducts.length}
              >
                Add selected products
                {this.state.pendingWarrantyProducts.length ? ` (${this.state.pendingWarrantyProducts.length})` : ''}
              </Button>,
              <Button key="done" type="primary" onClick={this.handleStoreWide}>
                Done
              </Button>,
            ]}
          >
            <WarrantyProducts
              warrantySelection={true}
              handleWarrantyProduct={this.handleWarrantyProduct}
              companyIdentifier={this.context.identifier}
              returnSettings={this.state.returnSettings}
              isMerchantDashboard={true}
              warrantyProducts={[...selectedProducts, ...this.state.pendingWarrantyProducts]}
              pendingWarrantyProducts={this.state.pendingWarrantyProducts}
              maxWarrantyProducts={MAX_WARRANTY_PRODUCTS}
              warrantySelectionCount={this.getWarrantySelectionCount()}
              handleToggleWarrantyProduct={this.handleToggleWarrantyProduct}
              handleToggleWarrantyProductVariants={this.handleToggleWarrantyProductVariants}
              handleToggleWarrantyProducts={this.handleToggleWarrantyProducts}
            />
          </Modal>
        )}
      </Form>
    );
  };

  // NOTE: the "Customize customer journey" (warranty notes / extra questions) section
  // that used to render here was removed - it's a global ReturnSetting.warrantyAdditionalFields
  // feature, not part of the Return Type redesign and not in this task's scope. Data on
  // existing companies is untouched; this only removes the dashboard entry point to edit
  // it from the Warranty Rules page. Revisit if/when per-rule additional fields (task Q8
  // backlog item) get built.

  render() {
    if (this.state.loading) {
      return (
        <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
          <div className="GlobalLoader GlobalLoader--md"></div>
        </div>
      );
    }

    const columns = [
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
      },
      {
        title: 'Rule Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Enable',
        key: 'enable',
        render: (_text, record) => (
          <Popconfirm
            title="Are you sure? This will affect warranty returns matching this rule."
            onConfirm={() => this.handleConfirm(record)}
            okText="Yes"
            cancelText="No"
          >
            <Switch checked={record.isActive} />
          </Popconfirm>
        ),
      },
      {
        title: 'Edit',
        key: 'edit',
        render: (_text, record) => (
          <Button ghost onClick={() => this.toggleEditMode(record)}>
            Edit rule
          </Button>
        ),
      },
      {
        title: 'Delete',
        key: 'delete',
        render: (_text, record) => (
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => this.deleteRule(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button ghost>Delete rule</Button>
          </Popconfirm>
        ),
      },
    ];

    return (
      <React.Fragment>
        <Row type="flex" justify="center">
          <Col span={22}>
            {this.state.showRuleForm ? (
              <React.Fragment>
                <Row type="flex" justify="end" className="u-marginBottom--md u-columnGap--sm">
                  <Button onClick={this.toggleRuleForm}>Cancel</Button>
                  <Button type="primary" onClick={this.handleSubmit}>
                    {this.state.enableEditMode ? 'Update' : 'Save changes'}
                  </Button>
                </Row>
                {this.renderRuleForm()}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Alert
                  className="u-marginBottom--lg"
                  message="Important: Warranties Rules"
                  description="All warranty rules take precedence over the Advanced Rules; in other words, they will override your active Advanced Rules if any are configured."
                  type="info"
                  showIcon
                />
                <div className="ContentPanel u-paddingTop--lg">
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 768 }}>
                      <Table
                        columns={columns}
                        dataSource={this.state.rules}
                        rowKey={(record) => record.id}
                        locale={{
                          emptyText: (
                            <div className="flex-row flex-middle-xxs flex-center-xxs">
                              <div className="flex-col-xxs-12">
                                <IconTray size="md" />
                              </div>
                              <div>
                                <p className="TextBody TextBody--xxxs">No Data</p>
                              </div>
                            </div>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
                <Row type="flex" justify="end" className="u-marginTop--sm u-marginBottom--md">
                  <Button type="primary" onClick={this.toggleRuleForm}>
                    Add Warranty Rule
                  </Button>
                </Row>
              </React.Fragment>
            )}
          </Col>
        </Row>
      </React.Fragment>
    );
  }
}

export default WarrantyRules;
